import React, { useEffect, useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

function Stars({ value = 0, size = 16, onSelect, className = "" }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className={`inline-flex items-center gap-0.5 ${className}`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          disabled={!onSelect}
          onMouseEnter={onSelect ? () => setHover(n) : undefined}
          onMouseLeave={onSelect ? () => setHover(0) : undefined}
          onClick={onSelect ? () => onSelect(n) : undefined}
          className={onSelect ? "transition-transform hover:scale-110 cursor-pointer" : "cursor-default"}
          aria-label={`${n} star`}
        >
          <Star
            style={{ width: size, height: size }}
            className={n <= active ? "fill-amber-400 text-amber-400" : "text-neutral-300"}
          />
        </button>
      ))}
    </div>
  );
}

export function RatingBadge({ average = 0, count = 0, size = 14, className = "" }) {
  if (!count) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-white/95 border border-neutral-200 shadow-sm px-2 py-0.5 text-[11px] font-semibold text-neutral-900 ${className}`} data-testid="rating-badge">
      <Star style={{ width: size, height: size }} className="fill-amber-400 text-amber-400" />
      {Number(average).toFixed(1)}
      <span className="text-neutral-500 font-normal">({count})</span>
    </span>
  );
}

export default function ReviewsBlock({ listingId, ownerId, currentUser }) {
  const [items, setItems] = useState([]);
  const [avg, setAvg] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  const isOwner = currentUser && ownerId && currentUser.id === ownerId;
  const canReview = !!currentUser && !isOwner;

  const load = async () => {
    try {
      const { data } = await api.get(`/listings/${listingId}/reviews`);
      setItems(data.items || []);
      setAvg(data.average || 0);
      setCount(data.count || 0);
      // Pre-fill user's own review if any
      if (currentUser) {
        const mine = (data.items || []).find((r) => r.userId === currentUser.id);
        if (mine) { setRating(mine.rating); setComment(mine.comment || ""); }
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [listingId, currentUser?.id]);

  const submit = async () => {
    if (!currentUser) { toast.error("Please login to leave a review"); return; }
    if (rating < 1) { toast.error("Please select a star rating"); return; }
    setSaving(true);
    try {
      await api.post(`/listings/${listingId}/reviews`, { rating, comment });
      toast.success("Thanks for your review!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit review");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-6 bg-card rounded-card border border-border shadow-soft p-5" data-testid="reviews-block">
      <div className="flex items-center gap-3 mb-1">
        <h2 className="font-display font-semibold text-ink">Ratings & Reviews</h2>
        {count > 0 && (
          <span className="inline-flex items-center gap-1 text-sm">
            <Stars value={Math.round(avg)} />
            <span className="font-semibold text-ink">{avg.toFixed(1)}</span>
            <span className="text-muted-foreground">({count})</span>
          </span>
        )}
      </div>
      {count === 0 && <p className="text-sm text-muted-foreground mb-3">No reviews yet — be the first to review.</p>}

      {canReview && (
        <div className="mt-3 border border-border rounded-xl p-4 bg-surface/40">
          <p className="text-sm font-medium text-ink mb-2">Your rating</p>
          <Stars value={rating} size={24} onSelect={setRating} />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Share your experience (optional)"
            className="mt-3 w-full rounded-md border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="review-comment"
          />
          <div className="mt-2 flex justify-end">
            <button onClick={submit} disabled={saving || rating < 1} className="inline-flex items-center gap-1.5 rounded-md bg-primary hover:bg-primary/90 disabled:opacity-50 text-white px-4 py-2 text-sm font-medium" data-testid="review-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {items.some((r) => r.userId === currentUser?.id) ? "Update review" : "Submit review"}
            </button>
          </div>
        </div>
      )}
      {isOwner && (
        <p className="text-xs text-muted-foreground mt-2">You can't review your own listing.</p>
      )}
      {!currentUser && (
        <p className="text-xs text-muted-foreground mt-2">Please login to leave a review.</p>
      )}

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : items.length > 0 ? (
        <ul className="mt-5 space-y-4">
          {items.map((r) => (
            <li key={r.id} className="border-t border-border pt-4">
              <div className="flex items-center gap-2">
                <Stars value={r.rating} size={14} />
                <span className="text-sm font-medium text-ink">{r.userName || "Anonymous"}</span>
                <span className="text-xs text-muted-foreground">{new Date(r.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
              </div>
              {r.comment && <p className="mt-1.5 text-sm text-ink/80 whitespace-pre-line">{r.comment}</p>}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
